import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { createPlanet, createRegion } from '../world/api.js';

/**
 * MAP-170..172. First run: name your domains, weigh them against each other,
 * name the main areas inside them.
 *
 * Deliberately short and entirely skippable. Everything here is editable later
 * through the ordinary UI, so nothing about this flow is a decision the person
 * is stuck with.
 */

const WORLDS = [
  { key: 'terra', name: 'Terra', description: 'Seven continents, varied' },
  { key: 'archipelago', name: 'Scattered Isles', description: 'Six islands' },
  { key: 'fractured', name: 'The Fracture', description: 'Six jagged lands' },
  { key: 'pangaea', name: 'The Whole', description: 'One supercontinent' },
  { key: 'polar', name: 'Coldharbour', description: 'Poles and belts' },
  { key: 'ringed', name: 'The Girdle', description: 'A band round the equator' },
];

interface DomainDraft {
  name: string;
  weight: number;
  art: string;
  areas: string;
}

const SUGGESTED: DomainDraft[] = [
  { name: 'Business', weight: 5, art: 'terra', areas: 'Finance, Clients, Product, Admin' },
  { name: 'Health', weight: 3, art: 'archipelago', areas: 'Training, Food, Medical' },
  { name: 'Home', weight: 3, art: 'fractured', areas: 'Maintenance, Money, Admin' },
  { name: 'People', weight: 2, art: 'polar', areas: 'Family, Friends' },
];

export function Setup({ onDone }: { onDone: () => void }) {
  const [domains, setDomains] = useState<DomainDraft[]>(SUGGESTED);
  const [step, setStep] = useState<'domains' | 'areas'>('domains');

  const update = (index: number, patch: Partial<DomainDraft>) =>
    setDomains((current) => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const build = useMutation({
    mutationFn: async () => {
      for (const domain of domains) {
        if (!domain.name.trim()) continue;
        const planet = await createPlanet(domain.name.trim(), domain.art, domain.weight);

        const areas = domain.areas
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean);

        for (const area of areas) {
          await createRegion({
            planetId: planet.id,
            kind: 'continent',
            name: area,
            importanceWeight: 1,
          });
        }
      }
    },
    onSuccess: onDone,
  });

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col justify-center gap-6 p-6">
      <header>
        <h1 className="text-2xl">
          {step === 'domains' ? 'What does your world consist of?' : 'What is inside them?'}
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          {step === 'domains'
            ? 'Each one becomes a planet. Size follows importance, so weigh them against each other rather than trying to be precise.'
            : 'The major areas of each domain, comma separated. These become its continents; anything finer can wait.'}
        </p>
      </header>

      <ul className="flex flex-col gap-3">
        {domains.map((domain, index) => (
          <li
            key={index}
            className="flex flex-col gap-3 rounded-[var(--radius-md)] p-3"
            style={{ background: 'var(--surface-1)' }}
          >
            {step === 'domains' ? (
              <div className="flex flex-wrap items-center gap-3">
                <input
                  value={domain.name}
                  onChange={(e) => update(index, { name: e.target.value })}
                  aria-label={`Domain ${index + 1} name`}
                  className="flex-1 rounded-[var(--radius-sm)] border border-border bg-surface-0 px-3 py-2 outline-none"
                />
                <label className="flex items-center gap-2 text-xs text-text-muted">
                  importance
                  <input
                    type="range"
                    min={1}
                    max={8}
                    value={domain.weight}
                    onChange={(e) => update(index, { weight: Number(e.target.value) })}
                    aria-label={`${domain.name} importance`}
                  />
                  <span className="w-4 text-text">{domain.weight}</span>
                </label>
                <select
                  value={domain.art}
                  onChange={(e) => update(index, { art: e.target.value })}
                  aria-label={`${domain.name} world`}
                  className="rounded-[var(--radius-sm)] border border-border bg-surface-0 px-2 py-2 text-sm"
                >
                  {WORLDS.map((world) => (
                    <option key={world.key} value={world.key}>
                      {world.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setDomains((c) => c.filter((_, i) => i !== index))}
                  aria-label={`Remove ${domain.name}`}
                  className="text-text-muted hover:text-text"
                >
                  ✕
                </button>
              </div>
            ) : (
              <label className="flex flex-col gap-1">
                <span className="text-sm">{domain.name}</span>
                <input
                  value={domain.areas}
                  onChange={(e) => update(index, { areas: e.target.value })}
                  aria-label={`${domain.name} areas`}
                  className="rounded-[var(--radius-sm)] border border-border bg-surface-0 px-3 py-2 outline-none"
                />
              </label>
            )}
          </li>
        ))}
      </ul>

      {step === 'domains' && (
        <button
          onClick={() =>
            setDomains((c) => [...c, { name: '', weight: 2, art: 'terra', areas: '' }])
          }
          className="self-start text-sm text-text-muted hover:text-text"
        >
          + another
        </button>
      )}

      <footer className="flex items-center gap-3">
        {step === 'domains' ? (
          <button
            onClick={() => setStep('areas')}
            disabled={domains.every((d) => !d.name.trim())}
            className="rounded-[var(--radius-sm)] bg-accent px-4 py-2 text-sm font-medium text-surface-0 disabled:opacity-50"
          >
            Next
          </button>
        ) : (
          <>
            <button onClick={() => setStep('domains')} className="text-sm text-text-muted hover:text-text">
              Back
            </button>
            <button
              onClick={() => build.mutate()}
              disabled={build.isPending}
              className="rounded-[var(--radius-sm)] bg-accent px-4 py-2 text-sm font-medium text-surface-0 disabled:opacity-50"
            >
              {build.isPending ? 'Building your world…' : 'Build it'}
            </button>
          </>
        )}

        <button onClick={onDone} className="ml-auto text-sm text-text-muted hover:text-text">
          Skip
        </button>
      </footer>

      {build.isError && (
        <p role="alert" className="text-sm" style={{ color: 'var(--health-4)' }}>
          That did not work. Nothing was lost — try again.
        </p>
      )}
    </div>
  );
}
